import {
    Alert,
    Box,
    Card,
    CardContent,
    CircularProgress,
    Container,
    Pagination,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import ApiClient from '../config/ApiClient';
import { ApiListResponse, NGU_EVENT_LABELS, NguEventType, NguPoints } from '../object-actions/types/types';

const PER_PAGE = 20;

/** Display label for an NGU event; uses NGU_EVENT_LABELS when available, else fallback formatting. */
function eventLabel(nguEvent: string | null): string {
    if (!nguEvent) return '—';
    const known = NGU_EVENT_LABELS[nguEvent as NguEventType];
    if (known) return known;
    return nguEvent
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');
}

/** Format ISO date string for display. */
function formatDate(iso: string): string {
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch {
        return iso;
    }
}

const MyProfilePointsScreen: React.FC = () => {
    const [results, setResults] = useState<NguPoints[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [grandTotal, setGrandTotal] = useState<number | null>(null);

    const fetchPoints = useCallback(async (pageNum: number) => {
        setLoading(true);
        setError(null);
        try {
            const offset = (pageNum - 1) * PER_PAGE;
            const params = new URLSearchParams({
                limit: PER_PAGE.toString(),
                offset: offset.toString()
            });
            const response = await ApiClient.get(`/api/my-points?${params.toString()}`);
            if (response.success && response.data) {
                const data = response.data as ApiListResponse<'NguPoints'>;
                setResults(data.results ?? []);
                setTotalCount(data.count ?? 0);
                const meta = data.meta as { grand_total?: number } | undefined;
                setGrandTotal(meta?.grand_total ?? null);
            } else {
                setError(response.error ?? 'Failed to load points');
            }
        } catch (err) {
            setError('Failed to load points');
            console.error('Error fetching points:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPoints(page);
    }, [page, fetchPoints]);

    const handlePageChange = (_event: React.ChangeEvent<unknown>, pageNum: number) => {
        setPage(pageNum);
    };

    const totalPages = Math.max(1, Math.ceil(totalCount / PER_PAGE));

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 2 }}>
                My Points
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                All points you have earned. Your grand total is shown below.
            </Typography>

            {grandTotal !== null && (
                <Card sx={{ mb: 3 }} data-attr={"grand-total-card"}>
                    <CardContent>
                        <Typography variant="overline" color="text.secondary">
                            Grand total
                        </Typography>
                        <Typography variant="h4" component="p" fontWeight={600}>
                            {grandTotal.toLocaleString()} pts
                        </Typography>
                    </CardContent>
                </Card>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Alert severity="error">{error}</Alert>
            ) : results.length === 0 ? (
                <Card>
                    <CardContent>
                        <Typography variant="body1" color="text.secondary" textAlign="center">
                            No points yet. Complete lessons and activities to earn points.
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <>
                    <TableContainer component={Card}>
                        <Table size="small" aria-label="Points history">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Activity</TableCell>
                                    <TableCell align="right">Points</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {results.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell>{formatDate(row.created_at)}</TableCell>
                                        <TableCell>{eventLabel(row.ngu_event)}</TableCell>
                                        <TableCell align="right">
                                            {row.n_points != null ? row.n_points.toLocaleString() : '—'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {totalCount > PER_PAGE && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={handlePageChange}
                                color="primary"
                            />
                        </Box>
                    )}

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                        Showing page {page} of {totalPages} ({totalCount} total)
                    </Typography>
                </>
            )}
        </Container>
    );
};

export default MyProfilePointsScreen;
